import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { bgGray, primaryBlack } from 'shared/theme/colors'
import * as yup from 'yup'

import { useUserProvider } from '../../../components/Context/UserContext'
import { PhoneInput } from '../../../components/MaskedInput/PhoneInput'
import { TextInput } from '../../../components/TextInput/TextInput'
import { convertMobileForAPI } from '../../../helpers/formatters'
import { useUserV3 } from '../../../hooks/query/useUserV3'
import { EmergencyContact, updateEmergencyContactInfo, User } from '../../../models/user'

interface ProfileEmergencyContactEditProps {
  handleClose: Handler
  user: User
}

const Schema = yup.object().shape({
  name: yup.string().required(),
  relationship: yup.string().required(),
  email: yup.string().required(),
  mobile: yup.string().required(),
})

interface Form {
  name: string
  relationship: string
  email: string
  mobile: string
}

export const ProfileEmergencyContactEdit = ({ handleClose, user }: ProfileEmergencyContactEditProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { showBanner, clearUserCache } = useUserProvider()
  const emergencyContact = user.driver?.emergencyContact
  const { refetch } = useUserV3(user.id, { enabled: false })

  const { control, handleSubmit } = useForm<Form>({
    mode: 'onSubmit',
    defaultValues: {
      name: emergencyContact?.name,
      relationship: emergencyContact?.relationship,
      email: emergencyContact?.email,
      mobile: emergencyContact?.mobile,
    },
    resolver: yupResolver(Schema),
  })

  const onSubmit = async (formData: Form) => {
    setIsLoading(true)
    try {
      const emergencyContact: EmergencyContact = {
        name: formData.name,
        mobile: convertMobileForAPI(formData.mobile),
        relationship: formData.relationship,
        email: formData.email,
      }

      await updateEmergencyContactInfo(user.id, emergencyContact)
      await Promise.all([clearUserCache(), refetch()])
      handleClose()
    } catch (err: any) {
      showBanner('error', err)
    }
    setIsLoading(false)
  }

  return (
    <SimpleDialog isOpen onClose={handleClose} size="small" alignText="left">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ width: '100%' }}>
          <Text variant="header2" fontColor={primaryBlack}>
            Emergency Contact
          </Text>
        </div>
        <Box mt={3} />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="Name" error={fieldState.error?.message} backgroundColor={bgGray} />
          )}
          name="name"
          control={control}
        />
        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="Relationship"
              error={fieldState.error?.message}
              backgroundColor={bgGray}
            />
          )}
          name="relationship"
          control={control}
        />
        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="Email" error={fieldState.error?.message} backgroundColor={bgGray} />
          )}
          name="email"
          control={control}
        />
        <Controller
          render={({ field, fieldState }) => (
            <PhoneInput
              {...field}
              label="Mobile Phone Number"
              error={fieldState.error?.message}
              backgroundColor={bgGray}
            />
          )}
          name="mobile"
          control={control}
        />

        <Box mt={5} />
        <Button type="submit" margin="0 auto" variant="primary" size="large" isLoading={isLoading}>
          Save
        </Button>
        <Box mt={3} />
        <Button variant="text" margin="0 auto" onClick={handleClose}>
          Cancel
        </Button>
      </form>
    </SimpleDialog>
  )
}
